import { create } from 'zustand';
import { pdfEngine } from '../core/PDFEngine';
import { useUIStore } from './uiStore';

export interface PDFDocumentInfo {
  id: string;
  filename: string;
  pageCount: number;
  fileSize: number;
  file: File | null;
  arrayBuffer: ArrayBuffer | null;
  isSaved: boolean;
  lastModified: number;
}

export interface DocumentHistoryEntry {
  arrayBuffer: ArrayBuffer;
  pageCount: number;
  currentPage: number;
  description: string;
}

interface DocumentState {
  documents: PDFDocumentInfo[];
  activeDocumentId: string | null;
  currentPage: number;
  totalPages: number;
  zoom: number;
  rotation: number;
  fitMode: 'page' | 'width' | 'actual' | 'custom';
  isLoading: boolean;
  loadingProgress: number;
  scrollMode: 'vertical' | 'horizontal' | 'single';
  spreadMode: 'none' | 'odd' | 'even';

  docHistory: Map<string, { undoStack: DocumentHistoryEntry[]; redoStack: DocumentHistoryEntry[] }>;

  addDocument: (doc: PDFDocumentInfo) => void;
  removeDocument: (id: string) => void;
  setActiveDocument: (id: string) => void;
  setCurrentPage: (page: number) => void;
  setTotalPages: (total: number) => void;
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  setRotation: (rotation: number) => void;
  rotateClockwise: () => void;
  rotateCounterClockwise: () => void;
  setFitMode: (mode: 'page' | 'width' | 'actual' | 'custom') => void;
  setIsLoading: (loading: boolean) => void;
  setLoadingProgress: (progress: number) => void;
  setScrollMode: (mode: 'vertical' | 'horizontal' | 'single') => void;
  setSpreadMode: (mode: 'none' | 'odd' | 'even') => void;
  nextPage: () => void;
  prevPage: () => void;
  goToPage: (page: number) => void;
  updateDocument: (id: string, updates: Partial<PDFDocumentInfo>) => void;

  pushDocHistory: (docId: string, description: string) => void;
  undoDocChange: () => Promise<boolean>;
  redoDocChange: () => Promise<boolean>;
  canUndoDocChange: () => boolean;
  canRedoDocChange: () => boolean;
}

const ZOOM_LEVELS = [0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5];

export const useDocumentStore = create<DocumentState>((set, get) => ({
  documents: [],
  activeDocumentId: null,
  currentPage: 1,
  totalPages: 0,
  zoom: 1,
  rotation: 0,
  fitMode: 'width',
  isLoading: false,
  loadingProgress: 0,
  scrollMode: 'vertical',
  spreadMode: 'none',
  docHistory: new Map(),

  addDocument: (doc) => {
    set((state) => ({
      documents: [...state.documents, doc],
      activeDocumentId: doc.id,
    }));
  },

  removeDocument: (id) => {
    set((state) => {
      const docs = state.documents.filter((d) => d.id !== id);
      const activeId = state.activeDocumentId === id
        ? (docs.length > 0 ? docs[docs.length - 1].id : null)
        : state.activeDocumentId;

      const newHistory = new Map(state.docHistory);
      newHistory.delete(id);

      return { documents: docs, activeDocumentId: activeId, docHistory: newHistory };
    });
  },

  setActiveDocument: (id) => set({ activeDocumentId: id, currentPage: 1 }),

  setCurrentPage: (page) => {
    const { totalPages } = get();
    if (page >= 1 && page <= totalPages) {
      set({ currentPage: page });
    }
  },

  setTotalPages: (total) => set({ totalPages: total }),
  setZoom: (zoom) => set({ zoom: Math.max(0.1, Math.min(10, zoom)), fitMode: 'custom' }),

  zoomIn: () => {
    const { zoom } = get();
    const nextLevel = ZOOM_LEVELS.find((z) => z > zoom + 0.01) || zoom * 1.25;
    set({ zoom: Math.min(5, nextLevel), fitMode: 'custom' });
  },

  zoomOut: () => {
    const { zoom } = get();
    const prevLevel = [...ZOOM_LEVELS].reverse().find((z) => z < zoom - 0.01) || zoom * 0.8;
    set({ zoom: Math.max(0.1, prevLevel), fitMode: 'custom' });
  },

  setRotation: (rotation) => set({ rotation: rotation % 360 }),
  rotateClockwise: () => set((s) => ({ rotation: (s.rotation + 90) % 360 })),
  rotateCounterClockwise: () => set((s) => ({ rotation: (s.rotation - 90 + 360) % 360 })),

  setFitMode: (mode) => set({ fitMode: mode }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setLoadingProgress: (progress) => set({ loadingProgress: progress }),
  setScrollMode: (mode) => set({ scrollMode: mode }),
  setSpreadMode: (mode) => set({ spreadMode: mode }),

  nextPage: () => {
    const { currentPage, totalPages } = get();
    if (currentPage < totalPages) set({ currentPage: currentPage + 1 });
  },

  prevPage: () => {
    const { currentPage } = get();
    if (currentPage > 1) set({ currentPage: currentPage - 1 });
  },

  goToPage: (page) => {
    const { totalPages } = get();
    set({ currentPage: Math.max(1, Math.min(totalPages, page)) });
  },

  updateDocument: (id, updates) => {
    set((state) => ({
      documents: state.documents.map((d) => (d.id === id ? { ...d, ...updates } : d)),
    }));
  },

  pushDocHistory: (docId, description) => {
    const doc = get().documents.find((d) => d.id === docId);
    if (!doc || !doc.arrayBuffer) return;

    const { docHistory, currentPage } = get();
    const history = docHistory.get(docId) || { undoStack: [], redoStack: [] };

    const entry: DocumentHistoryEntry = {
      arrayBuffer: doc.arrayBuffer.slice(0),
      pageCount: doc.pageCount,
      currentPage,
      description,
    };

    const newUndo = [...history.undoStack, entry].slice(-15);
    const newHistory = new Map(docHistory);
    newHistory.set(docId, {
      undoStack: newUndo,
      redoStack: [],
    });

    set({ docHistory: newHistory });
  },

  canUndoDocChange: () => {
    const { activeDocumentId, docHistory } = get();
    if (!activeDocumentId) return false;
    const history = docHistory.get(activeDocumentId);
    return !!history && history.undoStack.length > 0;
  },

  canRedoDocChange: () => {
    const { activeDocumentId, docHistory } = get();
    if (!activeDocumentId) return false;
    const history = docHistory.get(activeDocumentId);
    return !!history && history.redoStack.length > 0;
  },

  undoDocChange: async () => {
    const { activeDocumentId, documents, docHistory } = get();
    if (!activeDocumentId) return false;

    const doc = documents.find((d) => d.id === activeDocumentId);
    if (!doc || !doc.arrayBuffer) return false;

    const history = docHistory.get(activeDocumentId);
    if (!history || history.undoStack.length === 0) return false;

    const newUndoStack = [...history.undoStack];
    const prevEntry = newUndoStack.pop()!;

    const currentEntry: DocumentHistoryEntry = {
      arrayBuffer: doc.arrayBuffer.slice(0),
      pageCount: doc.pageCount,
      currentPage: get().currentPage,
      description: 'Current State',
    };
    const newRedoStack = [...history.redoStack, currentEntry];

    const newHistory = new Map(docHistory);
    newHistory.set(activeDocumentId, {
      undoStack: newUndoStack,
      redoStack: newRedoStack,
    });

    set({ docHistory: newHistory });

    await pdfEngine.loadDocument(prevEntry.arrayBuffer.slice(0));
    set((state) => ({
      documents: state.documents.map((d) =>
        d.id === activeDocumentId
          ? {
              ...d,
              arrayBuffer: prevEntry.arrayBuffer.slice(0),
              pageCount: prevEntry.pageCount,
              lastModified: Date.now(),
            }
          : d
      ),
      totalPages: prevEntry.pageCount,
      currentPage: Math.min(prevEntry.currentPage, prevEntry.pageCount),
    }));

    useUIStore.getState().addToast({ type: 'info', message: `↩️ Undid: ${prevEntry.description}` });
    return true;
  },

  redoDocChange: async () => {
    const { activeDocumentId, documents, docHistory } = get();
    if (!activeDocumentId) return false;

    const doc = documents.find((d) => d.id === activeDocumentId);
    if (!doc || !doc.arrayBuffer) return false;

    const history = docHistory.get(activeDocumentId);
    if (!history || history.redoStack.length === 0) return false;

    const newRedoStack = [...history.redoStack];
    const nextEntry = newRedoStack.pop()!;

    const currentEntry: DocumentHistoryEntry = {
      arrayBuffer: doc.arrayBuffer.slice(0),
      pageCount: doc.pageCount,
      currentPage: get().currentPage,
      description: 'Current State',
    };
    const newUndoStack = [...history.undoStack, currentEntry];

    const newHistory = new Map(docHistory);
    newHistory.set(activeDocumentId, {
      undoStack: newUndoStack,
      redoStack: newRedoStack,
    });

    set({ docHistory: newHistory });

    await pdfEngine.loadDocument(nextEntry.arrayBuffer.slice(0));
    set((state) => ({
      documents: state.documents.map((d) =>
        d.id === activeDocumentId
          ? {
              ...d,
              arrayBuffer: nextEntry.arrayBuffer.slice(0),
              pageCount: nextEntry.pageCount,
              lastModified: Date.now(),
            }
          : d
      ),
      totalPages: nextEntry.pageCount,
      currentPage: Math.min(nextEntry.currentPage, nextEntry.pageCount),
    }));

    useUIStore.getState().addToast({ type: 'info', message: `↪️ Redid document change` });
    return true;
  },
}));
